import { useQuery } from '@tanstack/react-query';
import api from '@/services/api';
import type { PaginatedResponse, Policy, PolicyFilters } from '@/types';

/**
 * Policy-list filters, including the two params the shared
 * `policyService.getPolicies` does not forward yet.
 *
 * `issue_date_from` / `issue_date_to` are `apps.policies.filters.PolicyFilter`'s
 * inclusive window on the issue date. The backend deliberately falls back to
 * `inception_date` for the rows where `issue_date` was never entered (a large
 * slice of the book: the Add Policy form treats it as optional), so a dated view
 * here agrees with the commission ledger and the broker reports, which bucket by
 * `Coalesce(issue_date, inception_date)` too.
 */
export interface PolicyListFilters extends PolicyFilters {
  /** `YYYY-MM-DD`, inclusive lower bound on the effective issue date. */
  issue_date_from?: string;
  /** `YYYY-MM-DD`, inclusive upper bound on the effective issue date. */
  issue_date_to?: string;
  /**
   * Board #254: whether an agent (POSP) is attached at all.
   *
   * Independent of `posp`, which names ONE agent. `posp` is the deep-link
   * scope (`/policies?posp=<id>` from an agent's page); this is the panel
   * dropdown. The backend applies each with its own `.filter()` call, so both
   * may be on the wire at once and neither replaces the other.
   */
  posp_assigned?: 'assigned' | 'not_assigned';
  /**
   * Board #279: which slice of the book to return.
   *
   * Omitted -- the server's default -- is live records only. `'true'` is
   * archived only; `'all'` is both. Deliberately tri-state rather than a
   * boolean: "show me only what I archived" and "show me everything" are
   * different questions, and a boolean could express only one of them.
   */
  archived?: 'true' | 'all';
  /**
   * U-04: DRF `OrderingFilter`'s param -- a field name, optionally with a
   * leading `-` for descending (`?ordering=-total_premium`).
   *
   * Only the names in `PolicyViewSet.ordering_fields` do anything; DRF drops
   * an unrecognised term SILENTLY, returning the default order rather than an
   * error, which is why PoliciesListPage marks only the two columns the server
   * can actually order by as sortable.
   */
  ordering?: string;
}

/**
 * Build the `GET /policies/` query string.
 *
 * Mirrors `policyService.getPolicies` (src/services/policies.ts) and adds
 * `issue_date_from` / `issue_date_to`. It lives here rather than in the service
 * because `src/services/**` is outside this task's file domain -- fold the two
 * extra params back into `policyService.getPolicies` and delete this module the
 * next time that file is edited.
 *
 * Every param only ever NARROWS the caller's already tenant-scoped book (the
 * scoping happens in `PolicyViewSet.get_queryset` before any filter runs), so
 * nothing here can widen what a broker/POSP/customer sees.
 */
export function buildPolicyListParams(page: number, filters?: PolicyListFilters): string {
  const params = new URLSearchParams();
  params.append('page', String(page));

  if (filters?.search) params.append('search', filters.search);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.display_status) params.append('display_status', filters.display_status);
  // B9: the list's Status filter speaks lifecycle (Upcoming / Active / Expired
  // / Cancelled). The backend 400s on an unknown value rather than ignoring it.
  if (filters?.lifecycle_status) params.append('lifecycle_status', filters.lifecycle_status);
  if (filters?.payment_status) params.append('payment_status', filters.payment_status);
  if (filters?.is_renewal !== undefined) params.append('is_renewal', String(filters.is_renewal));
  if (filters?.insurer) params.append('insurer', filters.insurer);
  if (filters?.insurance_type) params.append('insurance_type', filters.insurance_type);
  if (filters?.product) params.append('product', filters.product);
  if (filters?.issued !== undefined) params.append('issued', String(filters.issued));
  if (filters?.posp) params.append('posp', filters.posp);
  if (filters?.posp_assigned) params.append('posp_assigned', filters.posp_assigned);
  if (filters?.customer) params.append('customer', filters.customer);
  if (filters?.issue_date_from) params.append('issue_date_from', filters.issue_date_from);
  if (filters?.issue_date_to) params.append('issue_date_to', filters.issue_date_to);
  if (filters?.created_from) params.append('created_from', filters.created_from);
  if (filters?.created_to) params.append('created_to', filters.created_to);
  if (filters?.commission_status) params.append('commission_status', filters.commission_status);
  if (filters?.archived) params.append('archived', filters.archived);
  if (filters?.ordering) params.append('ordering', filters.ordering);
  if (filters?.page_size) params.append('page_size', String(filters.page_size));

  return params.toString();
}

/**
 * The policy list query used by PoliciesListPage.
 *
 * The query key keeps the `['policies', ...]` prefix that `useMarkPaymentReceived`
 * and the policy mutations invalidate, so this stays in step with the rest of the
 * app exactly as `usePolicies` does.
 */
export function usePolicyList(page: number = 1, filters?: PolicyListFilters) {
  return useQuery({
    queryKey: ['policies', page, filters],
    queryFn: async () => {
      const response = await api.get<PaginatedResponse<Policy>>(
        `/policies/?${buildPolicyListParams(page, filters)}`
      );
      return response.data;
    },
    placeholderData: (previousData) => previousData,
  });
}
